import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ChildItemsGrid } from 'src/shared/components/organisms/childItemsGrid';
import { PageTemplate } from 'src/shared/templates/pageTemplate';
import { ContentPageAPI } from 'src/shared/services/api/contentPageAPI';
import SkeletonCards from 'src/shared/components/skeletons/skeletonCards';
import { IParams } from 'src/shared/interfaces/params';
import { ContentPageResponse } from 'src/shared/services/api/response/contentPageResponse';
import { ActionButtons } from 'src/shared/components/atoms/actionButtons';
import { fileUrl } from 'src/shared/services/utils/url';
import { GenerateActionButtonIcons } from 'src/shared/components/atoms/generateActionButtonsIcons';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { RootStateType } from 'src/shared/services/redux/reducers';
import {
  GetAdminPermission,
  useCanViewStats,
} from 'src/shared/helpers/permissionHelper';
import { useTranslation } from 'react-i18next';
import { StatsRangeBadge } from 'src/shared/components/atoms/statsRangeBadge';
import { useContentStats } from 'src/shared/helpers/hooks/useContentStats';
import { useLogContentView } from 'src/shared/helpers/hooks/useLogContentView';

const MenuPage = () => {
  const { id } = useParams<IParams>();
  const { t } = useTranslation();
  // Global states
  const { user } = useSelector((state: RootStateType) => state.oidc);

  // Local states
  const [loading, setLoading] = useState<boolean>(true);
  const [item, setItem] = useState<ContentPageResponse | undefined>();
  const [selectedImage, setSelectedImage] = useState<string | undefined>();
  const canViewStats = useCanViewStats(item?.companyId);
  const [statsRangeDays, setStatsRangeDays] = useState<number>(365);

  const getContentPageById = useCallback(async () => {
    setLoading(true);
    try {
      const contentApi = new ContentPageAPI();
      const res = await contentApi.Get(id);
      if (res) {
        setItem(res?.result);
        await setSelectedImage(
          fileUrl(res?.result?.featuredImageDocumentId, user?.access_token),
        );
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }, [id, user?.access_token]);

  useEffect(() => {
    getContentPageById();
  }, [getContentPageById]);

  const {
    allViews,
    uniqueViews,
    refetch: refetchContentStats,
  } = useContentStats({
    contentPageId: item?.id,
    rangeDays: statsRangeDays,
    enabled: canViewStats,
  });

  useLogContentView({
    contentPageId: item?.id,
    enabled: true,
    onLogged: refetchContentStats,
  });

  // Check if page is a parent without content
  const isParentThatHasChildren =
    item?.canHaveChildren && item?.childItems && item.childItems.length > 0;

  // const isParentWithoutContent =
  //   item?.canHaveChildren &&
  //   (!item?.content || item.content.trim() === '') &&
  //   item?.childItems &&
  //   item.childItems.length > 0;

  return (
    <PageTemplate
      header={item?.title ?? ''}
      headerMeta={
        canViewStats ? (
          <StatsRangeBadge
            label={t('ViewsLabel')}
            rangeDays={statsRangeDays}
            value={allViews}
            uniqueValue={uniqueViews}
            onRangeChange={setStatsRangeDays}
          />
        ) : undefined
      }
      featuredImage={selectedImage}
      featuredImageText={item?.featuredImageText ?? ''}
      onImageError={() => setSelectedImage(undefined)}
      headerContent={item?.content ?? ''}
      showTags
      tags={item?.tags}
      businessAreaTags={item?.businessAreaTags}
      content={
        <>
          {loading && <SkeletonCards rows={1} />}
          {!loading && item && (
            <>
              {/* Show child items grid if page is parent without content */}
              {isParentThatHasChildren && (
                <ChildItemsGrid childItems={item.childItems ?? []} />
              )}
            </>
          )}
          <ActionButtons
            items={GenerateActionButtonIcons(
              // Create
              undefined,
              // Edit
              GetAdminPermission(item?.companyId)
                ? `/MenuPage/${id}/edit`
                : undefined,
              // Pin
              GetAdminPermission(item?.companyId)
                ? () => ConsoleHelper.log('Pin item', id)
                : undefined,
            )}
          />
        </>
      }
    />
  );
};

export default MenuPage;
